import type { IngestionState } from "./ingestion.types";
import type { UploadFailure, UploadJobIntent } from "./uploadJobContract";

/**
 * The durable job will eventually provide these fields. Keeping the UI model
 * small lets the first activity surface work with fixtures without inventing
 * a second lifecycle vocabulary.
 */
export type DocumentProcessingItem = {
  id: string;
  name: string;
  detail: string;
  state: IngestionState;
  progress?: number;
  /** Stable failure metadata; UI resolves copy locally from the failure code. */
  failure?: UploadFailure | null;
  /** Set when this row is backed by a durable UploadJob/FileItem snapshot. */
  durable?: boolean;
  /** The upload intent controls whether completion needs patient review. */
  intent?: UploadJobIntent;
  jobId?: string;
  fileItemId?: string;
};

export const DOCUMENT_ACTIVITY_FIXTURES = [
  "processing",
  "large-handoff",
  "review-ready",
  "partial-success",
  "validation-errors",
  "non-retryable-outcomes",
] as const;

export type DocumentActivityFixture =
  (typeof DOCUMENT_ACTIVITY_FIXTURES)[number];

const PROCESSING_FIXTURE: DocumentProcessingItem[] = [
  {
    id: "amh-lab-report",
    name: "amh-lab-report.pdf",
    detail: "4.2 MB · Uploading",
    progress: 68,
    state: "uploading",
  },
  {
    id: "afc-ultrasound",
    name: "afc-ultrasound.pdf",
    detail: "8.1 MB · Upload complete",
    state: "processing",
  },
  {
    id: "treatment-plan",
    name: "treatment-plan.pdf",
    detail: "2.6 MB · Waiting to upload",
    state: "queued",
  },
];

const LARGE_HANDOFF_FIXTURE: DocumentProcessingItem[] = [
  {
    id: "clinic-records-packet",
    name: "clinic-records-packet.pdf",
    detail: "42 pages · Source saved",
    state: "background_processing",
  },
  {
    id: "outside-results",
    name: "outside-results.pdf",
    detail: "18 pages · Source saved",
    state: "background_processing",
  },
  {
    id: "insurance-card",
    name: "insurance-card-front.jpg",
    detail: "1.8 MB · Ready to review",
    state: "review_ready",
  },
];

const REVIEW_READY_FIXTURE: DocumentProcessingItem[] = [
  {
    id: "treatment-plan-ready",
    name: "treatment-plan.pdf",
    detail: "3 proposed updates",
    state: "review_ready",
    durable: true,
    intent: "prefill",
  },
  {
    id: "appointment-letter-ready",
    name: "appointment-letter.pdf",
    detail: "1 proposed update",
    state: "review_ready",
    durable: true,
    intent: "prefill",
  },
  {
    id: "source-document-ready",
    name: "scanned-clinic-notes.pdf",
    detail: "Original file saved",
    state: "source_only",
  },
];

const PARTIAL_SUCCESS_FIXTURE: DocumentProcessingItem[] = [
  {
    id: "saved-lab-report",
    name: "amh-lab-report.pdf",
    detail: "Saved to your records",
    state: "complete",
  },
  {
    id: "unreadable-ultrasound",
    name: "afc-ultrasound.pdf",
    detail: "Action needed",
    state: "failed_retryable",
    failure: {
      code: "extraction_failed",
      checkpoint: "processing",
      retryable: true,
      messageKey: "documents.upload.error_failed_process",
    },
  },
  {
    id: "insurance-review",
    name: "insurance-card-front.jpg",
    detail: "Ready to review",
    state: "needs_review",
    durable: true,
    intent: "prefill",
  },
];

const VALIDATION_ERRORS_FIXTURE: DocumentProcessingItem[] = [
  {
    id: "unsupported-archive",
    name: "clinic-records.zip",
    detail: "Action needed",
    state: "unsupported",
    failure: {
      code: "unsupported_type",
      checkpoint: "validation",
      retryable: false,
      messageKey: "documents.upload.failure.unsupported_type.description",
    },
  },
  {
    id: "stalled-records",
    name: "outside-records.pdf",
    detail: "Action needed",
    state: "failed_retryable",
    failure: {
      code: "network_stalled",
      checkpoint: "upload",
      retryable: true,
      messageKey: "documents.upload.failure.network_stalled.description",
    },
  },
  {
    id: "saved-record",
    name: "medication-list.pdf",
    detail: "Saved to your records",
    state: "complete",
  },
];

const NON_RETRYABLE_OUTCOMES_FIXTURE: DocumentProcessingItem[] = [
  {
    id: "already-saved-record",
    name: "already-saved-record.pdf",
    detail: "Action needed",
    state: "unsupported",
    failure: {
      code: "duplicate",
      checkpoint: "validation",
      retryable: false,
      messageKey: "documents.upload.failure.duplicate.description",
    },
  },
  {
    id: "canceled-record",
    name: "canceled-record.pdf",
    detail: "Action needed",
    state: "rejected",
    failure: {
      code: "canceled",
      checkpoint: "upload",
      retryable: false,
      messageKey: "documents.upload.failure.canceled.description",
    },
  },
  {
    id: "ready-record",
    name: "ready-record.pdf",
    detail: "Saved to your records",
    state: "complete",
  },
];

export function createDocumentProcessingFixture(
  fixture: DocumentActivityFixture,
): DocumentProcessingItem[] {
  const source =
    fixture === "large-handoff"
      ? LARGE_HANDOFF_FIXTURE
      : fixture === "review-ready"
        ? REVIEW_READY_FIXTURE
        : fixture === "partial-success"
          ? PARTIAL_SUCCESS_FIXTURE
          : fixture === "validation-errors"
            ? VALIDATION_ERRORS_FIXTURE
            : fixture === "non-retryable-outcomes"
              ? NON_RETRYABLE_OUTCOMES_FIXTURE
              : PROCESSING_FIXTURE;

  return source.map((item) => ({ ...item }));
}

export function hasVisibleDocumentActivity(
  items: readonly DocumentProcessingItem[],
): boolean {
  return items.some(
    (item) =>
      item.state !== "idle" &&
      item.state !== "complete" &&
      item.state !== "canceled",
  );
}

export type DocumentActivitySummary = {
  activeCount: number;
  attentionCount: number;
  failureCount: number;
  retryableFailureCount: number;
  permanentFailureCount: number;
  readyCount: number;
  hasPartialSuccess: boolean;
};

export function summarizeDocumentActivity(
  items: readonly DocumentProcessingItem[],
): DocumentActivitySummary {
  const visibleItems = items.filter(
    (item) =>
      item.state !== "idle" &&
      item.state !== "complete" &&
      item.state !== "canceled",
  );
  const failures = visibleItems.filter((item) => item.failure);
  const hasNonFailureItem = items.some(
    (item) =>
      !item.failure && item.state !== "idle" && item.state !== "canceled",
  );
  const readyCount = items.filter(
    (item) =>
      item.state === "complete" ||
      item.state === "source_only" ||
      item.state === "review_ready" ||
      item.state === "needs_review",
  ).length;

  return {
    activeCount: visibleItems.filter((item) =>
      isDocumentActivityActive(item.state),
    ).length,
    attentionCount: visibleItems.filter((item) =>
      isDocumentActivityAttention(item.state),
    ).length,
    failureCount: failures.length,
    retryableFailureCount: failures.filter((item) => item.failure?.retryable)
      .length,
    permanentFailureCount: failures.filter((item) => !item.failure?.retryable)
      .length,
    readyCount,
    hasPartialSuccess: failures.length > 0 && hasNonFailureItem,
  };
}

export function isDocumentActivityActive(state: IngestionState): boolean {
  return (
    state === "selecting" ||
    state === "validating" ||
    state === "queued" ||
    state === "uploading" ||
    state === "uploaded" ||
    state === "processing" ||
    state === "background_processing" ||
    state === "saving"
  );
}

export function isDocumentActivityAttention(state: IngestionState): boolean {
  return (
    state === "review_ready" ||
    state === "needs_review" ||
    state === "partial_success" ||
    state === "failed_retryable" ||
    state === "unsupported" ||
    state === "rejected" ||
    state === "source_only"
  );
}
